import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { useAuth } from '../../hooks/useAuth';
import { useUnitats } from '../../hooks/useUnitats';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	WideContentContainer,
} from '../Shared/CommonStyles';

import * as S from './ClassificationOverview.styles';

// ─── Types ─────────────────────────────────────────────────────────────────

interface UnitatBreakdownEntry {
	unitat: string;
	weightedScore: number;
	count: number;
}

interface SchoolYearBreakdownEntry {
	schoolYear: string;
	count: number;
}

interface AlbumOverview {
	albumId: string;
	albumName: string;
	uploadDate: string;
	sampleImage?: string;
	schoolYear?: string;
	pictureCount?: number;
	totalClassifications: number;
	unitatBreakdown: UnitatBreakdownEntry[];
	schoolYearBreakdown: SchoolYearBreakdownEntry[];
	topUnitat: UnitatBreakdownEntry | null;
	topSchoolYear: SchoolYearBreakdownEntry | null;
	averageConfidence: number;
}

interface OverviewResponse {
	albums: AlbumOverview[];
	totalAlbums: number;
	totalClassifications: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const UNITAT_COLORS: Record<string, string> = {
	cills: '#e74c3c',
	llids: '#3498db',
	rings: '#2ecc71',
	pics: '#f39c12',
	trucs: '#9b59b6',
	agrupament: '#1abc9c',
	varis: '#95a5a6',
};

const getUnitatColor = (unitat: string): string =>
	UNITAT_COLORS[unitat] || '#95a5a6';

const getUnitatDisplayName = (
	unitat: string,
	unitats: Array<{ abreviacio: string; nom: string }> | undefined | null,
): string => {
	if (unitat === 'agrupament') return 'Agrupament';
	if (unitat === 'varis') return 'Varis';
	const found = unitats?.find((u) => u.abreviacio.toLowerCase() === unitat);
	return found?.nom || unitat;
};

// ─── Storage key ───────────────────────────────────────────────────────────

const HIDDEN_KEY = 'classification-overview-hidden';

const loadHiddenIds = (): Set<string> => {
	try {
		const raw = localStorage.getItem(HIDDEN_KEY);
		if (!raw) return new Set();
		return new Set(JSON.parse(raw));
	} catch {
		return new Set();
	}
};

const saveHiddenIds = (ids: Set<string>) => {
	localStorage.setItem(HIDDEN_KEY, JSON.stringify([...ids]));
};

// ─── Component ─────────────────────────────────────────────────────────────

const ClassificationOverview: React.FC = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { unitats } = useUnitats();

	const [hiddenIds, setHiddenIds] = useState<Set<string>>(loadHiddenIds);

	const {
		data,
		isLoading,
		error,
	} = useFetch<OverviewResponse>({
		queryKey: ['classification-overview'],
		url: '/api/galeria/albums/classification-overview',
	});

	const toggleShowAll = useCallback(() => {
		setHiddenIds(new Set());
		localStorage.removeItem(HIDDEN_KEY);
	}, []);

	const hideAlbum = useCallback((albumId: string) => {
		setHiddenIds((prev) => {
			const next = new Set(prev);
			next.add(albumId);
			saveHiddenIds(next);
			return next;
		});
	}, []);

	// Sort albums: ones with classifications for BOTH a specific schoolYear
	// (other than 'varis') AND a specific unitat (other than 'varis') show first.
	// Within each group, sort by totalClassifications descending.
	const sortedAlbums = useMemo(() => {
		if (!data?.albums) return [];

		const hasSpecificUnitat = (album: AlbumOverview) =>
			album.unitatBreakdown.some((u) => u.unitat !== 'varis');
		const hasSpecificSchoolYear = (album: AlbumOverview) =>
			album.schoolYearBreakdown.some((y) => y.schoolYear !== 'varis');

		return [...data.albums].sort((a, b) => {
			const aPriority =
				hasSpecificUnitat(a) && hasSpecificSchoolYear(a) ? 1 : 0;
			const bPriority =
				hasSpecificUnitat(b) && hasSpecificSchoolYear(b) ? 1 : 0;

			if (aPriority !== bPriority) {
				return bPriority - aPriority; // higher priority first
			}
			return b.totalClassifications - a.totalClassifications;
		});
	}, [data]);

	const visibleAlbums = useMemo(
		() => sortedAlbums.filter((a) => !hiddenIds.has(a.albumId)),
		[sortedAlbums, hiddenIds],
	);

	// Guard: only Admin / Agrupament
	const isAllowed =
		!!user &&
		(user.roles.includes('Admin') || user.roles.includes('Agrupament'));

	if (!isAllowed) {
		return (
			<WideContentContainer>
				<ErrorMessage>
					No tens permís per veure aquesta pàgina.
				</ErrorMessage>
			</WideContentContainer>
		);
	}

	return (
		<WideContentContainer>
			<S.Container>
				{/* Header */}
				<S.HeaderRow>
					<div>
						<S.Title>Classificacions rebudes</S.Title>
						<S.Subtitle>
							Visió global de totes les classificacions enviades
							pels Caps i Excaps
						</S.Subtitle>
					</div>
					<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						{hiddenIds.size > 0 && (
							<S.ShowAllButton onClick={toggleShowAll}>
								👁️ Mostra{' '}
								{hiddenIds.size === 1
									? 'l\'àlbum amagat'
									: `els ${hiddenIds.size} àlbums amagats`}
							</S.ShowAllButton>
						)}
						<S.BackButton onClick={() => navigate('/galeria/classificar')}>
							← Tornar a &quot;Per classificar&quot;
						</S.BackButton>
					</div>
				</S.HeaderRow>

				{/* Loading */}
				{isLoading && (
					<S.LoadingContainer>
						<LoadingGif size="large" />
					</S.LoadingContainer>
				)}

				{/* Error */}
				{error && !isLoading && (
					<ErrorMessage>
						Error: {extractErrorMessage(error)}
					</ErrorMessage>
				)}

				{/* Data */}
				{!isLoading && !error && data && (
					<>
						{/* Summary cards */}
						<S.SummaryGrid>
							<S.SummaryCard>
								<S.SummaryLabel>
									Àlbums amb classificacions
								</S.SummaryLabel>
								<S.SummaryValue>
									{data.totalAlbums}
								</S.SummaryValue>
								<S.SummarySub>
									{data.totalAlbums === 1
										? 'pendent de revisar'
										: 'pendents de revisar'}
								</S.SummarySub>
							</S.SummaryCard>
							<S.SummaryCard>
								<S.SummaryLabel>
									Total respostes
								</S.SummaryLabel>
								<S.SummaryValue>
									{data.totalClassifications}
								</S.SummaryValue>
								<S.SummarySub>
									{data.totalClassifications === 1
										? 'classificació rebuda'
										: 'classificacions rebudes'}
								</S.SummarySub>
							</S.SummaryCard>
							<S.SummaryCard>
								<S.SummaryLabel>
									Mitjana de respostes
								</S.SummaryLabel>
								<S.SummaryValue>
									{data.totalAlbums > 0
										? Math.round(
												data.totalClassifications /
													data.totalAlbums,
										  )
										: 0}
								</S.SummaryValue>
								<S.SummarySub>per àlbum</S.SummarySub>
							</S.SummaryCard>
						</S.SummaryGrid>

						{/* Album list */}
						{visibleAlbums.length === 0 ? (
							<S.EmptyState>
								<S.EmptyIcon>
									{hiddenIds.size === sortedAlbums.length
										? '🙈'
										: '📭'}
								</S.EmptyIcon>
								<div>
									<strong>
										{hiddenIds.size === sortedAlbums.length
											? 'Tots els àlbums estan amagats'
											: 'No hi ha classificacions'}
									</strong>
								</div>
								<div style={{ marginTop: 8 }}>
									{hiddenIds.size === sortedAlbums.length
										? 'Fes clic a "Mostra tots els àlbums amagats" per tornar-los a veure.'
										: 'Quan els Caps o Excaps classifiquin àlbums provisionals, apareixeran aquí.'}
								</div>
							</S.EmptyState>
						) : (
							<S.AlbumList>
								{visibleAlbums.map((album) => (
									<S.AlbumCardWrapper key={album.albumId}>
										<S.HideButton
											title="Amaga aquest àlbum"
											onClick={(e) => {
												e.stopPropagation();
												hideAlbum(album.albumId);
											}}
										>
											🙈
										</S.HideButton>
										<S.AlbumCard
											onClick={() =>
												navigate(
													`/galeria/classificar/${album.albumId}`,
												)
											}
										>
											{/* Thumbnail */}
											<S.Thumbnail
												$imageUrl={album.sampleImage}
											>
												{!album.sampleImage && '📷'}
											</S.Thumbnail>

										{/* Info */}
										<S.AlbumInfo>
											<S.AlbumName>
												{album.albumName}
											</S.AlbumName>

											<S.AlbumMeta>
												<span>
													📅{' '}
													{new Date(
														album.uploadDate,
													).toLocaleDateString(
														'ca-ES',
														{
															year: 'numeric',
															month: 'long',
															day: 'numeric',
														},
													)}
												</span>
												{album.pictureCount != null && (
													<span>
														🖼️{' '}
														{album.pictureCount}{' '}
														{album.pictureCount === 1
															? 'foto'
															: 'fotos'}
													</span>
												)}
												<span>
													📋{' '}
													{album.totalClassifications}{' '}
													{album.totalClassifications ===
													1
														? 'classificació'
														: 'classificacions'}
												</span>
												<span>
													🎯{' '}
													{album.averageConfidence}%
													confiança
												</span>
											</S.AlbumMeta>

											{/* Unitat breakdown bars */}
											{album.unitatBreakdown.length >
												0 && (
												<S.BreakdownSection>
													<S.BreakdownLabel>
														Vots per unitat
													</S.BreakdownLabel>
													{album.unitatBreakdown.map(
														(u) => {
															const maxScore =
																Math.max(
																	...album.unitatBreakdown.map(
																		(x) =>
																			x.weightedScore,
																	),
																);
															return (
																<S.UnitatRow
																	key={u.unitat}
																>
																	<S.UnitatBadge
																		$unitat={
																			u.unitat
																		}
																	>
																		{getUnitatDisplayName(
																			u.unitat,
																			unitats,
																		)}
																	</S.UnitatBadge>
																	<S.WeightBarContainer>
																		<S.WeightBar
																			$width={
																				(u.weightedScore /
																					Math.max(
																						maxScore,
																						1,
																					)) *
																				100
																			}
																			$color={getUnitatColor(
																				u.unitat,
																			)}
																		/>
																	</S.WeightBarContainer>
																	<S.WeightValue>
																		{
																			u.weightedScore
																		}
																		%
																	</S.WeightValue>
																	<span
																		style={{
																			fontSize:
																				'0.75rem',
																			color: '#999',
																			minWidth: 60,
																		}}
																	>
																		(
																		{u.count}{' '}
																		{u.count ===
																		1
																			? 'vot'
																			: 'vots'}
																		)
																	</span>
																</S.UnitatRow>
															);
														},
													)}
												</S.BreakdownSection>
											)}

											{/* School year chips */}
											{album.schoolYearBreakdown
												.length > 0 && (
												<S.BreakdownSection>
													<S.BreakdownLabel>
														Cursos proposats
													</S.BreakdownLabel>
													<S.YearChipsContainer>
														{album.schoolYearBreakdown.map(
															(y) => (
																<S.YearChip
																	key={
																		y.schoolYear
																	}
																>
																	{
																		y.schoolYear
																	}{' '}
																	<S.YearChipCount>
																		(
																		{y.count}
																		)
																	</S.YearChipCount>
																</S.YearChip>
															),
														)}
													</S.YearChipsContainer>
												</S.BreakdownSection>
											)}
										</S.AlbumInfo>

										{/* Arrow */}
										<S.ViewArrow>›</S.ViewArrow>
									</S.AlbumCard>
									</S.AlbumCardWrapper>
								))}
							</S.AlbumList>
						)}
					</>
				)}
			</S.Container>
		</WideContentContainer>
	);
};

export default ClassificationOverview;
